import React, { useState, useEffect } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { BarChart2, TrendingUp, PieChart as PieIcon, Activity, Trophy } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';


const COLORS = ['#00E5FF', '#7B2FBE', '#10B981', '#F59E0B', '#EF4444', '#06B6D4', '#8B5CF6', '#EC4899'];
const DATE_RANGES = [{ label: '7 Days', days: 7 }, { label: '30 Days', days: 30 }, { label: '90 Days', days: 90 }];
const tooltipStyle = { background: '#0D1B2A', border: '1px solid rgba(0,229,255,0.2)', borderRadius: '12px', fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#E2E8F0' };

function StatBadge({ label, value, color }) {
  return (
    <div className="rounded-xl px-4 py-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,229,255,0.08)' }}>
      <p className="text-xs" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{label}</p>
      <p className="text-lg font-bold mt-0.5" style={{ color: color || '#E2E8F0', fontFamily: 'DM Sans, sans-serif' }}>{value}</p>
    </div>
  );
}

export default function ScanAnalytics({ partnerId }) {
  const [range, setRange] = useState(30);
  const [loading, setLoading] = useState(true);
  const [topProducts, setTopProducts] = useState([]);
  const [dailyScans, setDailyScans] = useState([]);
  const [categoryDist, setCategoryDist] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [summary, setSummary] = useState({ total: 0, avgDaily: 0, peakDay: '—', topConverter: '—' });

  const fetchAnalytics = async () => {
    if (!partnerId) return;
    setLoading(true);
    try {
      const since = new Date();
      since?.setDate(since?.getDate() - range);

      const { data: scans } = await supabase
        ?.from('product_scans')
        ?.select('id, action_taken, scanned_at, retail_products(id, product_name, category, price)')
        ?.eq('partner_id', partnerId)
        ?.gte('scanned_at', since?.toISOString());

      if (!scans) { setLoading(false); return; }

      // Product map
      const productMap = {};
      scans?.forEach(s => {
        const pid = s?.retail_products?.id;
        if (!pid) return;
        if (!productMap?.[pid]) productMap[pid] = { name: s?.retail_products?.product_name, scans: 0, purchases: 0, category: s?.retail_products?.category, price: s?.retail_products?.price };
        productMap[pid].scans++;
        if (s?.action_taken === 'purchased') productMap[pid].purchases++;
      });

      const sorted = Object.values(productMap)?.sort((a, b) => b?.scans - a?.scans);

      // Top 10 products
      setTopProducts(sorted?.slice(0, 10)?.map(p => ({
        name: p?.name?.length > 16 ? p?.name?.substring(0, 16) + '…' : p?.name,
        scans: p?.scans,
      })));

      // Daily scan volume
      const dayMap = {};
      for (let i = range - 1; i >= 0; i--) {
        const d = new Date(); d?.setDate(d?.getDate() - i);
        const key = d?.toISOString()?.split('T')?.[0];
        dayMap[key] = 0;
      }
      scans?.forEach(s => {
        const key = s?.scanned_at?.split('T')?.[0];
        if (key && dayMap?.[key] !== undefined) dayMap[key]++;
      });
      const dailyArr = Object.entries(dayMap)?.map(([date, count]) => ({ date: date?.slice(5), count }));
      setDailyScans(dailyArr);

      // Category distribution
      const catMap = {};
      scans?.forEach(s => {
        const cat = s?.retail_products?.category || 'Other';
        catMap[cat] = (catMap?.[cat] || 0) + 1;
      });
      setCategoryDist(Object.entries(catMap)?.map(([name, value]) => ({ name, value })));

      // Leaderboard
      const lb = Object.values(productMap)?.map(p => ({
        ...p,
        interestRate: p?.scans > 0 ? Math.round((p?.purchases / p?.scans) * 100) : 0,
        conversionRate: p?.scans > 0 ? Math.round((p?.purchases / p?.scans) * 100) : 0,
        avgBudgetImpact: p?.price ? `$${parseFloat(p?.price)?.toFixed(0)}` : '—',
      }))?.sort((a, b) => b?.scans - a?.scans);
      setLeaderboard(lb);

      // Summary stats
      const peakEntry = dailyArr?.reduce((max, d) => d?.count > max?.count ? d : max, { date: '—', count: 0 });
      const topConv = lb?.filter(p => p?.scans >= 3)?.sort((a, b) => b?.conversionRate - a?.conversionRate)?.[0];
      setSummary({
        total: scans?.length,
        avgDaily: range > 0 ? Math.round(scans?.length / range) : 0,
        peakDay: peakEntry?.date || '—',
        topConverter: topConv?.name || '—',
      });
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { fetchAnalytics(); }, [partnerId, range]);

  function ChartCard({ title, icon: Icon, children }) {
    return (
      <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,229,255,0.08)' }}>
        <div className="flex items-center gap-2 mb-4">
          <Icon size={16} style={{ color: '#00E5FF' }} />
          <h3 className="text-sm font-semibold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>{title}</h3>
        </div>
        {loading ? <div className="h-52 rounded-xl animate-pulse" style={{ background: 'rgba(255,255,255,0.03)' }} /> : children}
      </div>
    );
  }

  const EmptyChart = () => (
    <div className="flex flex-col items-center justify-center h-52 gap-2">
      <Activity size={24} style={{ color: '#1E3A5F' }} />
      <p className="text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>No data for this period</p>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Scan Analytics</h2>
          <p className="text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Product performance & consumer engagement</p>
        </div>
        <div className="flex gap-2">
          {DATE_RANGES?.map(r => (
            <button key={r?.days} onClick={() => setRange(r?.days)}
              className="px-3 py-1.5 rounded-xl text-xs font-medium transition-all"
              style={{
                background: range === r?.days ? 'rgba(0,229,255,0.12)' : 'rgba(0,0,0,0.2)',
                border: range === r?.days ? '1px solid rgba(0,229,255,0.3)' : '1px solid rgba(255,255,255,0.06)',
                color: range === r?.days ? '#00E5FF' : '#64748B',
                fontFamily: 'DM Sans, sans-serif',
              }}>{r?.label}</button>
          ))}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatBadge label="Total Scans" value={loading ? '...' : summary?.total} color="#00E5FF" />
        <StatBadge label="Avg Daily Scans" value={loading ? '...' : summary?.avgDaily} color="#7B2FBE" />
        <StatBadge label="Peak Scan Day" value={loading ? '...' : summary?.peakDay} color="#F59E0B" />
        <StatBadge label="Top Converting" value={loading ? '...' : (summary?.topConverter?.length > 14 ? summary?.topConverter?.substring(0, 14) + '…' : summary?.topConverter)} color="#10B981" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Top 10 Products Bar Chart */}
        <ChartCard title="Top 10 Most Scanned Products" icon={BarChart2}>
          {topProducts?.length === 0 ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={topProducts} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" horizontal={false} />
                <XAxis type="number" tick={{ fill: '#64748B', fontSize: 10, fontFamily: 'DM Sans' }} />
                <YAxis type="category" dataKey="name" tick={{ fill: '#94A3B8', fontSize: 10, fontFamily: 'DM Sans' }} width={90} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="scans" fill="#00E5FF" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        {/* Daily Scan Volume Line Chart */}
        <ChartCard title="Daily Scan Volume" icon={TrendingUp}>
          {dailyScans?.every(d => d?.count === 0) ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={dailyScans} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="date" tick={{ fill: '#64748B', fontSize: 10, fontFamily: 'DM Sans' }} interval={Math.floor(dailyScans?.length / 6)} />
                <YAxis tick={{ fill: '#64748B', fontSize: 10, fontFamily: 'DM Sans' }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Line type="monotone" dataKey="count" stroke="#7B2FBE" strokeWidth={2} dot={false} activeDot={{ r: 4, fill: '#7B2FBE' }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        {/* Category Pie Chart */}
        <ChartCard title="Scan Distribution by Category" icon={PieIcon}>
          {categoryDist?.length === 0 ? <EmptyChart /> : (
            <div className="flex items-center gap-4">
              <ResponsiveContainer width="55%" height={180}>
                <PieChart>
                  <Pie data={categoryDist} cx="50%" cy="50%" innerRadius={45} outerRadius={75} dataKey="value" paddingAngle={3}>
                    {categoryDist?.map((_, i) => <Cell key={i} fill={COLORS?.[i % COLORS?.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-1.5 overflow-y-auto max-h-44">
                {categoryDist?.map((c, i) => (
                  <div key={c?.name} className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: COLORS?.[i % COLORS?.length] }} />
                    <span className="text-xs flex-1 truncate" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>{c?.name}</span>
                    <span className="text-xs" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{c?.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </ChartCard>

        {/* Leaderboard */}
        <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(0,229,255,0.08)' }}>
          <div className="px-5 py-3 flex items-center gap-2" style={{ background: 'rgba(0,0,0,0.3)', borderBottom: '1px solid rgba(0,229,255,0.08)' }}>
            <Trophy size={15} style={{ color: '#F59E0B' }} />
            <h3 className="text-sm font-semibold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Scan Leaderboard</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ background: 'rgba(0,0,0,0.2)', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  {['Product', 'Cat.', 'Scans', 'Interest', 'Avg Price', 'Conv.']?.map(h => (
                    <th key={h} className="px-3 py-2.5 text-left text-xs font-medium" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(4)]?.map((_, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                      {[...Array(6)]?.map((_, j) => <td key={j} className="px-3 py-2.5"><div className="h-3 rounded animate-pulse" style={{ background: 'rgba(255,255,255,0.05)', width: '60px' }} /></td>)}
                    </tr>
                  ))
                ) : leaderboard?.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>No scan data</td></tr>
                ) : (
                  leaderboard?.map((p, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)', background: 'rgba(255,255,255,0.01)' }}>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-1.5">
                          {i < 3 && <span className="text-xs" style={{ color: ['#F59E0B', '#94A3B8', '#CD7F32']?.[i] }}>{['🥇', '🥈', '🥉']?.[i]}</span>}
                          <span className="text-xs text-white" style={{ fontFamily: 'DM Sans, sans-serif', maxWidth: '90px', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p?.name}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-xs" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>{p?.category?.substring(0, 8)}</td>
                      <td className="px-3 py-2.5 text-xs font-semibold" style={{ color: '#00E5FF', fontFamily: 'DM Sans, sans-serif' }}>{p?.scans}</td>
                      <td className="px-3 py-2.5 text-xs" style={{ color: '#F59E0B', fontFamily: 'DM Sans, sans-serif' }}>{p?.interestRate}%</td>
                      <td className="px-3 py-2.5 text-xs" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>{p?.avgBudgetImpact}</td>
                      <td className="px-3 py-2.5 text-xs font-medium" style={{ color: p?.conversionRate > 20 ? '#10B981' : '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{p?.conversionRate}%</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
